import { useCallback, useMemo } from 'react';
import type {
  EnvironmentKey,
  EntityDetailSchema,
  EntityStatus,
  EntityStatusFilter,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { useAbortController } from '../use-abort-controller/use-abort-controller';
import { useGetAllEntitiesOfType } from '../use-get-all-entities-of-type/use-get-all-entities-of-type';
import {
  useCopyEntitiesToEnvironment,
  getCopyTargetFromSession,
  type CopyTargetOverride,
} from '../use-copy-entities-to-environment/use-copy-entities-to-environment';
import { buildOperationErrorDetail } from '../../utilities/build-operation-error-detail/build-operation-error-detail';
import {
  createBulkActionReportingStore,
  type BulkActionReportingStore,
} from '../../bulk-action-reporting/bulk-action-reporting.store';
import { createPaginatedFetchProgressHandlers } from '../../bulk-action-reporting/create-paginated-fetch-progress-handlers';
import { withBestEffortReporting } from '../../bulk-action-reporting/with-best-effort-reporting';

export interface UseCopyAllEntitiesToEnvironmentBulkActionProps {
  /** The domain entity type to copy (e.g., 'config-activity') */
  domainEntityType: string;
  /** Human-readable name of the entity type for display in messages */
  domainEntityName: string;
  /** The environment key from which entities will be read */
  sourceEnvironment: EnvironmentKey;
  /** The environment key to which entities will be copied */
  targetEnvironment: EnvironmentKey;
  /** Human-readable name of the target environment */
  targetEnvironmentName: string;
  /** The JSON schema describing the entity structure, used for resolving relationships */
  schema: EntityDetailSchema;
  /**
   * The tenant ID to capture errors with.
   * @param tenantId - The tenant ID to capture errors with.
   */
  tenantId: string;
  /**
   * Optional Zustand store for session-based reporting.
   */
  bulkActionReportingStore?: BulkActionReportingStore;
}

/**
 * Hook that returns a callback to copy all entities of a specific type from
 * the source environment to the target environment.
 *
 * This performs a copy to environment operation: for each entity, all referenced
 * entities are also copied to the target environment.
 */
export const useCopyAllEntitiesToEnvironmentBulkAction = ({
  domainEntityType,
  domainEntityName,
  sourceEnvironment,
  targetEnvironment: configuredTargetEnvironment,
  targetEnvironmentName: configuredTargetEnvironmentName,
  schema,
  tenantId,
  bulkActionReportingStore,
}: UseCopyAllEntitiesToEnvironmentBulkActionProps) => {
  const { formatMessage } = useIntl();

  const reportingStore = useMemo(
    () => bulkActionReportingStore ?? createBulkActionReportingStore(),
    [bulkActionReportingStore],
  );

  const getAllEntitiesOfType = useGetAllEntitiesOfType({
    environment: sourceEnvironment,
    domainEntityType,
  });

  const { copyEntitiesToEnvironment, cancelCopy, retryFailedCopy } =
    useCopyEntitiesToEnvironment({
      sourceEnvironment,
      targetEnvironment: configuredTargetEnvironment,
      schema,
      targetEnvironmentName: configuredTargetEnvironmentName,
      domainEntityType,
      tenantId,
      bulkActionReportingStore: reportingStore,
    });

  const { createAbortController, abort } = useAbortController();

  const copyAllEntitiesToEnvironment = useCallback(
    async ({
      status: statusFilter = 'published',
      target: targetOverride,
    }: {
      status?: EntityStatusFilter;
      /**
       * Copy to this environment instead of the configured one. Taken per call for the same
       * reason useCopyEntitiesToEnvironment takes it per call: a list page offers one
       * "Copy all to X" per target, and the click has to run against the one just picked.
       */
      target?: CopyTargetOverride;
    } = {}) => {
      const targetEnvironment =
        targetOverride?.environmentKey ?? configuredTargetEnvironment;
      const targetEnvironmentName =
        targetOverride?.name ?? configuredTargetEnvironmentName;
      const abortController = createAbortController();

      // Open the session up-front so the modal renders immediately and the
      // catch path below always has somewhere to surface failures —
      // including ones that happen before the first page is streamed.
      withBestEffortReporting(() => {
        reportingStore.getState().createSession({
          operationType: 'copyToEnvironment',
          domainEntityType,
          sourceEnvironment,
          targetEnvironment,
          metadata: {
            tenantId,
            targetEnvironmentName,
            statusFilter: Array.isArray(statusFilter)
              ? statusFilter.join(',')
              : statusFilter,
          },
        });
      });

      const streamingHandlers = createPaginatedFetchProgressHandlers({
        reportingStore,
        domainEntityType,
      });

      try {
        const { entities } = await getAllEntitiesOfType(
          {
            ...streamingHandlers,
            onEntityFetchFailed: async ({ entityId, entityName, error }) => {
              const entityError = buildOperationErrorDetail(
                error,
                formatMessage,
              );

              withBestEffortReporting(() => {
                reportingStore.getState().recordItemResult({
                  sourceEntityId: entityId,
                  entityType: domainEntityType,
                  entityName,
                  status: 'error',
                  error: entityError,
                });
              });
            },
            onEntityRetrying: async ({ entityId }) => {
              withBestEffortReporting(() => {
                reportingStore.getState().recordItemResult({
                  sourceEntityId: entityId,
                  entityType: domainEntityType,
                  status: 'loading',
                });
              });
            },
          },
          abortController.signal,
          {
            status: statusFilter,
            // Always allow partial results — per-entity failures are
            // surfaced through the reporting store, not by aborting the
            // whole batch.
            allowPartialResults: true,
          },
        );

        // Step 2: Copy all fetched entities to the target environment. The override is
        // handed on so the inner copy -- and the session it reports against -- describes the
        // same environment this call opened its session for.
        await copyEntitiesToEnvironment(entities, targetOverride);

        // copyEntitiesToEnvironment skips completeSession when an existing
        // session is present (so retry callers can own the lifecycle). The
        // copy-all flow owns the session, so it's responsible for completing.
        withBestEffortReporting(() => {
          reportingStore.getState().completeSession();
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        withBestEffortReporting(() => {
          reportingStore.getState().failSession(
            error instanceof Error
              ? error.message
              : formatMessage(
                  { id: 'BULK_PROMOTE_ALL_ERROR_TITLE' },
                  {
                    entityName: domainEntityName,
                    environmentName: targetEnvironmentName,
                  },
                ),
          );
        });
      }
    },
    [
      createAbortController,
      formatMessage,
      domainEntityName,
      domainEntityType,
      sourceEnvironment,
      configuredTargetEnvironment,
      configuredTargetEnvironmentName,
      tenantId,
      getAllEntitiesOfType,
      copyEntitiesToEnvironment,
      reportingStore,
    ],
  );

  const retryFailedCopyAll = useCallback(async () => {
    const { session } = reportingStore.getState();
    // Recover the status filter from the original session so retries
    // use the same "include drafts" choice the user made.
    // The status filter is stored as a comma-joined string (e.g. "draft,published").
    // EntityStatus values never contain commas, so splitting on comma is safe.
    const rawStatusFilter = session?.metadata?.statusFilter;
    const savedStatusFilter: EntityStatusFilter | undefined =
      rawStatusFilter?.includes(',')
        ? (rawStatusFilter.split(',') as EntityStatus[])
        : (rawStatusFilter as EntityStatus | undefined);

    // No prior session — nothing to resume, run a fresh copy-all.
    // savedStatusFilter is undefined here (no session to recover it from),
    // so this falls back to the default published-only filter.
    if (!session) {
      await copyAllEntitiesToEnvironment({ status: savedStatusFilter });
      return;
    }

    // Recovered from the session for the same reason as the status filter: a retry has to
    // land where the original run went, not on the configured target.
    const savedTarget = getCopyTargetFromSession(session);

    const errorItems = session.itemResults.filter(
      (item) => item.status === 'error',
    );
    if (errorItems.length === 0) {
      await copyAllEntitiesToEnvironment({
        status: savedStatusFilter,
        target: savedTarget,
      });
      return;
    }

    try {
      await retryFailedCopy();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      withBestEffortReporting(() => {
        reportingStore
          .getState()
          .failSession(error instanceof Error ? error.message : 'Retry failed');
      });

      throw error;
    }
  }, [reportingStore, copyAllEntitiesToEnvironment, retryFailedCopy]);

  const cancelCopyAll = useCallback(() => {
    abort();
    cancelCopy();
  }, [abort, cancelCopy]);

  return { copyAllEntitiesToEnvironment, cancelCopyAll, retryFailedCopyAll };
};
